// Financial Freedom Calculator -- the left-pane input form.
//
// Handles the core scalar inputs AND the two dynamic cash-flow arrays
// (lump_sums / other_incomes). Because Step 4 exposed `updateInput`, we mutate
// the arrays immutably here without needing the raw `setInputs` dispatcher.
//
// Currency is a purely presentational, frontend-only concern (see
// utils/currency): a global visitor picks their currency and every monetary
// slider prefix + answer-card figure updates instantly.

import React from 'react';

import type {
    FinancialFreedomInputs,
    LumpSum,
    OtherIncome,
} from '../types';
import { Periodicity, WithdrawalPeriodicity } from '../types';
import { CURRENCIES, currencySymbol } from '../utils/currency';
import { SliderInput } from './SliderInput';

interface InputsPanelProps {
    inputs: FinancialFreedomInputs;
    updateInput: <K extends keyof FinancialFreedomInputs>(
        field: K,
        value: FinancialFreedomInputs[K],
    ) => void;
    currency: string;
    onCurrencyChange: (code: string) => void;
}

const titleCase = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ');

export const InputsPanel: React.FC<InputsPanelProps> = ({
    inputs,
    updateInput,
    currency,
    onCurrencyChange,
}) => {
    const sym = currencySymbol(currency);

    // Array mutators for dynamic cash flows.
    const addLumpSum = () =>
        updateInput('lump_sums', [
            ...inputs.lump_sums,
            { amount: 50000, age_received: inputs.current_age + 5 },
        ]);
    const removeLumpSum = (idx: number) =>
        updateInput(
            'lump_sums',
            inputs.lump_sums.filter((_, i) => i !== idx),
        );
    const updateLumpSum = (idx: number, field: keyof LumpSum, value: number) => {
        const arr = [...inputs.lump_sums];
        arr[idx] = { ...arr[idx], [field]: value };
        updateInput('lump_sums', arr);
    };

    const addOtherIncome = () =>
        updateInput('other_incomes', [
            ...inputs.other_incomes,
            {
                amount: 1000,
                periodicity: Periodicity.MONTHLY,
                start_age: inputs.current_age,
                end_age: 95,
            },
        ]);
    const removeOtherIncome = (idx: number) =>
        updateInput(
            'other_incomes',
            inputs.other_incomes.filter((_, i) => i !== idx),
        );
    const updateOtherIncome = (
        idx: number,
        field: keyof OtherIncome,
        value: OtherIncome[keyof OtherIncome],
    ) => {
        const arr = [...inputs.other_incomes];
        arr[idx] = { ...arr[idx], [field]: value };
        updateInput('other_incomes', arr);
    };

    return (
        <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-md border border-slate-200 flex flex-col gap-8 h-full">
            {/* Currency + Demographics */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Choose:
                    </h3>
                    <div className="flex items-center gap-2">
                        <label
                            htmlFor="ff-currency"
                            className="text-xs font-semibold text-slate-500"
                        >
                            Currency
                        </label>
                        <select
                            id="ff-currency"
                            value={currency}
                            onChange={(e) => onCurrencyChange(e.target.value)}
                            className="border border-slate-300 rounded-md p-1.5 text-sm bg-white text-slate-900 focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                        >
                            {CURRENCIES.map((c) => (
                                <option key={c.code} value={c.code}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <SliderInput
                    label="Current Age"
                    value={inputs.current_age}
                    min={18}
                    max={80}
                    onChange={(v) => updateInput('current_age', v)}
                    suffix=" yrs"
                />
                <SliderInput
                    label="Total Savings Today"
                    value={inputs.total_savings_today}
                    min={0}
                    max={5000000}
                    step={5000}
                    onChange={(v) => updateInput('total_savings_today', v)}
                    prefix={sym}
                />
                <SliderInput
                    label="Stable Annual Income"
                    value={inputs.stable_annual_income}
                    min={0}
                    max={1000000}
                    step={1000}
                    onChange={(v) => updateInput('stable_annual_income', v)}
                    prefix={sym}
                />
                <SliderInput
                    label="Annual Savings Rate"
                    value={inputs.annual_savings_rate}
                    min={0}
                    max={100}
                    step={1}
                    onChange={(v) => updateInput('annual_savings_rate', v)}
                    suffix="%"
                />
            </div>

            {/* Retirement Goals */}
            <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 border-t border-slate-100 pt-6">
                    Retirement Goals
                </h3>
                <SliderInput
                    label="Target Retirement Age"
                    value={inputs.target_retirement_age ?? inputs.current_age + 10}
                    min={inputs.current_age}
                    max={95}
                    onChange={(v) => updateInput('target_retirement_age', v)}
                    suffix=" yrs"
                />
                <SliderInput
                    label="Desired Withdrawal"
                    value={inputs.desired_withdrawal_amount}
                    min={1000}
                    max={100000}
                    step={500}
                    onChange={(v) => updateInput('desired_withdrawal_amount', v)}
                    prefix={sym}
                />

                <div className="flex flex-col mt-2">
                    <label className="text-sm font-semibold text-slate-700 mb-1">
                        Withdrawal Frequency
                    </label>
                    <select
                        value={inputs.withdrawal_periodicity}
                        onChange={(e) =>
                            updateInput(
                                'withdrawal_periodicity',
                                e.target.value as FinancialFreedomInputs['withdrawal_periodicity'],
                            )
                        }
                        className="border border-slate-300 rounded-md p-2 text-sm bg-white text-slate-900 focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                    >
                        {Object.values(WithdrawalPeriodicity).map((wp) => (
                            <option key={wp} value={wp}>
                                {titleCase(wp)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Dynamic Arrays: Lump Sums & Other Incomes */}
            <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 border-t border-slate-100 pt-6">
                    Additional Cash Flows
                </h3>

                {/* Lump Sums */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-slate-700">
                            Lump Sums (e.g. Inheritance)
                        </span>
                        <button
                            type="button"
                            onClick={addLumpSum}
                            className="text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
                        >
                            + Add
                        </button>
                    </div>
                    {inputs.lump_sums.map((ls, idx) => (
                        <div
                            key={idx}
                            className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-3 relative"
                        >
                            <button
                                type="button"
                                onClick={() => removeLumpSum(idx)}
                                aria-label="Remove lump sum"
                                className="absolute top-2 right-3 text-lg text-slate-400 hover:text-red-500 leading-none"
                            >
                                &times;
                            </button>
                            <SliderInput
                                label="Amount"
                                value={ls.amount}
                                min={0}
                                max={2000000}
                                step={5000}
                                onChange={(v) => updateLumpSum(idx, 'amount', v)}
                                prefix={sym}
                            />
                            <SliderInput
                                label="Age Received"
                                value={ls.age_received}
                                min={inputs.current_age}
                                max={95}
                                onChange={(v) => updateLumpSum(idx, 'age_received', v)}
                                suffix=" yrs"
                            />
                        </div>
                    ))}
                </div>

                {/* Other Incomes */}
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-slate-700">
                            Other Incomes (e.g. Side Hustle)
                        </span>
                        <button
                            type="button"
                            onClick={addOtherIncome}
                            className="text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
                        >
                            + Add
                        </button>
                    </div>
                    {inputs.other_incomes.map((oi, idx) => (
                        <div
                            key={idx}
                            className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-3 relative"
                        >
                            <button
                                type="button"
                                onClick={() => removeOtherIncome(idx)}
                                aria-label="Remove other income"
                                className="absolute top-2 right-3 text-lg text-slate-400 hover:text-red-500 leading-none"
                            >
                                &times;
                            </button>
                            <SliderInput
                                label="Amount"
                                value={oi.amount}
                                min={0}
                                max={50000}
                                step={500}
                                onChange={(v) => updateOtherIncome(idx, 'amount', v)}
                                prefix={sym}
                            />

                            <div className="mb-4">
                                <label className="block text-xs font-semibold text-slate-700 mb-1">
                                    Frequency
                                </label>
                                <select
                                    value={oi.periodicity}
                                    onChange={(e) =>
                                        updateOtherIncome(
                                            idx,
                                            'periodicity',
                                            e.target.value as OtherIncome['periodicity'],
                                        )
                                    }
                                    className="w-full text-sm p-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                                >
                                    {Object.values(Periodicity).map((p) => (
                                        <option key={p} value={p}>
                                            {titleCase(p)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                                        Start Age
                                    </label>
                                    <input
                                        type="number"
                                        value={oi.start_age}
                                        onChange={(e) =>
                                            updateOtherIncome(
                                                idx,
                                                'start_age',
                                                Number(e.target.value),
                                            )
                                        }
                                        className="w-full text-sm p-2 border border-slate-300 rounded-md text-slate-900 focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                                        End Age
                                    </label>
                                    <input
                                        type="number"
                                        value={oi.end_age}
                                        onChange={(e) =>
                                            updateOtherIncome(
                                                idx,
                                                'end_age',
                                                Number(e.target.value),
                                            )
                                        }
                                        className="w-full text-sm p-2 border border-slate-300 rounded-md text-slate-900 focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
